import { InjectionToken } from '@angular/core';

import { AuthFailureCause } from '../model/failure';

/**
 * The sole owner of persisted secrets on a device, and nothing else.
 *
 * Six keys are written here, because that is the set Amplify needs back before it will refresh — see
 * {@link DURABLE_KEY_PATTERN}. None of them is access-controlled and no read prompts, which is why
 * there is one `read` rather than the `read`/`unlock` pair this interface used to carry: whether
 * someone is about to see a prompt is no longer a question about storage. The biometric gates a
 * stored password instead, which is replayed into an ordinary sign-in and reaches nothing in here —
 * see `BiometricSignIn`, and ADR 0003.
 *
 * Everything else the app holds lives in memory and is re-derived — see ADR 0003.
 */
export interface Vault {
  /**
   * Read. Never prompts.
   *
   * `null` means the Vault holds nothing under this key, and only that. Every other outcome rejects,
   * because absence and an unreadable Vault route to opposite places: one is a launch with no session
   * to restore, the other is a device the app has to give up on.
   */
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  keys(): Promise<readonly string[]>;

  /**
   * Empty the whole namespace, and reject if that did not happen.
   *
   * The one destructive operation, and the only one that treats a failure as one. `remove` and
   * `keys` are tolerant on purpose, because they serve a launch that has to reach a decided state;
   * a purge serves a Sign-out, where the tolerant answer is the dangerous one — "cleared" about a
   * device still holding the Durable Root.
   *
   * It cannot be built from `keys` plus `remove` for exactly that reason: both swallow, so a clear
   * assembled from them cannot fail. Nothing waits on the rejection either, so it buys one thing,
   * which is the thing that matters — `auth.command_failed`, and someone learning the device kept
   * the session.
   */
  purge(): Promise<void>;
}

export const VAULT = new InjectionToken<Vault>('Vault');

/**
 * An error from the Vault, already classified so callers never pattern-match on messages.
 *
 * The field is `failure`, not `cause`: `Error.cause` is the standard slot for the underlying
 * error in a chain, and this is a classification rather than a wrapped error. Reusing the name
 * would shadow it and invite the wrong assumption at every catch site.
 */
export class VaultError extends Error {
  constructor(
    readonly failure: AuthFailureCause,
    message?: string
  ) {
    super(message ?? failure.kind);
    this.name = 'VaultError';
  }
}

/**
 * The classification a Vault already made, or the safest reading of an error it did not raise.
 *
 * The machine routes on this: every `biometric*` cause leaves the Durable Root intact and lands on the
 * sign-in form with something to tap, while `vaultUnavailable` gives up on the device and clears it.
 * Collapsing them would put a transient failure and a revoked credential on the same code path.
 */
export function vaultCause(error: unknown): AuthFailureCause {
  return error instanceof VaultError ? error.failure : { kind: 'vaultUnavailable' };
}

/**
 * Which of Amplify's storage keys must survive a relaunch.
 *
 * Amplify writes its whole token set through whatever storage it is given, and this is the set it
 * needs back before it will refresh. Each entry earns its place for a different reason, none of
 * them "the app wants to read it" — every Resource Token is still re-derived on every launch:
 *
 * - `refreshToken` is the Durable Root, the one credential that outlives its own lifetime.
 * - `accessToken` is a **session marker**. `DefaultTokenStore.loadTokens()` throws
 *   `NoSessionFoundException` when it is absent and `TokenOrchestrator.getTokens` swallows that to
 *   `null`, so without it Amplify reports no session and never reaches the network. Its expiry is
 *   never checked on load, and Session Restore forces a refresh before anything reads it.
 * - `LastAuthUser` is not a secret, but it is how Amplify reconstructs every other key name, so
 *   without it a stored refresh token is unreachable.
 * - `deviceKey`, `deviceGroupKey` and `randomPasswordKey` because this user pool tracks devices.
 *   Sign-in ends with a `ConfirmDevice` call, and Cognito then binds the refresh token to that
 *   device: `refreshAuthTokens` sends `DeviceKey: tokens.deviceMetadata?.deviceKey`, and a refresh
 *   without it comes back `NotAuthorizedException: Invalid Refresh Token`. All three are needed
 *   rather than just `deviceKey`, because `getDeviceMetadata()` returns `null` unless it can read
 *   the whole trio.
 *
 * The invariant this preserves is not "exactly one persisted secret" — that is not implementable
 * against Amplify's token store. It is that **no persisted credential is ever used as-is**. See
 * ADR 0003, and `amplify-session-contract.spec.ts`, which pins this set against Amplify itself.
 */
export const DURABLE_KEY_PATTERN =
  /\.(refreshToken|accessToken|LastAuthUser|deviceKey|deviceGroupKey|randomPasswordKey)$/;

export function isDurableKey(key: string): boolean {
  return DURABLE_KEY_PATTERN.test(key);
}

/**
 * Which durable key is the Durable Root. Exactly one.
 *
 * Nothing about storage turns on this — every entry is protected identically — and no screen asks it
 * any more either: the sign-in screen's button is offered off the *keychain* enrollment, which is a
 * separate item with a separate lifetime, so the Vault is deliberately not consulted there. See
 * `LoginCredentialsComponent`.
 *
 * It survives because it names the one key the other five are useless without, which is the
 * distinction `hasSession` is built on.
 */
export const DURABLE_ROOT_KEY_PATTERN = /\.refreshToken$/;

export function isDurableRootKey(key: string): boolean {
  return DURABLE_ROOT_KEY_PATTERN.test(key);
}

/**
 * The key naming the person every other key belongs to.
 *
 * The only durable key that is *not* under the user prefix — Amplify writes it as
 * `CognitoIdentityServiceProvider.<clientId>.LastAuthUser` — which is what makes it the entry point
 * rather than one more entry in a required set. Its own name carries the client scope and its value
 * carries the username, and the two joined are the prefix every other key sits under.
 */
export const LAST_AUTH_USER_SUFFIX = '.LastAuthUser';

/**
 * The keys without which Amplify reports no session, read under one person's prefix.
 *
 * `loadTokens()` needs the access token to believe a session exists, and a session with no refresh
 * token cannot be renewed — so a set missing either is not a session, and a Session Restore that
 * started on one could not succeed.
 *
 * **Under one prefix, never matched loose across the store.** A device that has held two sessions
 * holds keys under two prefixes and does so legitimately: `storeTokens` writes the new
 * `LastAuthUser` and the new person's keys without removing the previous person's, and `clearTokens`
 * leaves the device trio behind even for the person it clears. A set assembled across prefixes can
 * therefore name a session Amplify cannot load — every key present, none of them the ones
 * `LastAuthUser` points at.
 */
export const REQUIRED_SESSION_KEY_SUFFIXES = ['.refreshToken', '.accessToken'] as const;

/**
 * The device trio, which is all-or-nothing.
 *
 * `getDeviceMetadata()` returns `null` unless it can read all three, and a `null` there means
 * `refreshAuthTokens` sends no `DeviceKey` — which on a pool that tracks devices Cognito answers
 * with `NotAuthorizedException: Invalid Refresh Token`. A partial trio is therefore always a
 * storage gap, and it is the one gap that would otherwise reach the server and come back looking
 * like a revoked credential.
 *
 * Absent entirely is a different matter and legitimate: a pool with device tracking off never
 * writes these at all.
 */
export const DEVICE_KEY_SUFFIXES = ['.deviceKey', '.deviceGroupKey', '.randomPasswordKey'] as const;
